import { DatePipe, Location } from '@angular/common';
import { Injectable, NgZone, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Account } from '@df/account/account.interfaces';
import { ICallableFunction, RequestService } from '@df/api/request/request.service';
import { LocalStorage } from '@df/browser/storage/decorator/local-storage';
import { Category, ICategoryDataV2, ICategoryDataV3 } from '@df/catalog/category/category';
import { IPage, IPageDataV3 } from '@df/catalog/cms/cms-model.interface';
import { ICrumb } from '@df/catalog/model';
import { IProductDataV2, IProductDataV3, Product } from '@df/catalog/product/product';
import { Stock } from '@df/catalog/stock/stock';
import { CheckoutFormComponentDef } from '@df/checkout/checkout-form.component.def';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { App } from '@df/core/app';
import { Meta } from '@df/core/meta/meta';
import { routeOverride } from '@df/core/meta/route-override.pipe';
import { MultisiteService } from '@df/core/service/multisite';
import { TimeoutService } from '@df/ng/timeout.service';
import { SearchResult } from '@df/search/search-result';
import { SearchResultEvent } from '@df/search/search-result-event';
import { SearchService } from '@df/search/search.service';
import { Basket, IBasketData } from '@df/session/api/basket/basket';
import { BasketItem } from '@df/session/api/basket/basket-item';
import {
    BasketService,
    IAddToBasketSuccessEventData,
    IChangeQtyBasketItemSuccessEventData,
    IRemoveFromBasketSuccessEventData
} from '@df/session/api/basket/basket.service';
import { CustomerModel } from '@df/session/api/customer.model';
import { IAddToWishlistData, WISHLIST_ADD_SUCCESS_EVENT, WISHLIST_REMOVE_SUCCESS_EVENT } from '@df/session/api/wishlist/wishlist';
import { NewsletterChangeEvent } from '@df/session/newsletter/newsletter-change.event';
import { NewsletterUpdateEvent } from '@df/session/newsletter/newsletter-update.event';
import { Newsletter } from '@df/session/newsletter/newsletter.interfaces';
import { NewsletterService } from '@df/session/newsletter/newsletter.service';
import { toDate } from '@df/util/fn/to-date';
import cloneDeep from 'lodash-es/cloneDeep';
import compact from 'lodash-es/compact';
import each from 'lodash-es/each';
import find from 'lodash-es/find';
import isArray from 'lodash-es/isArray';
import pickBy from 'lodash-es/pickBy';
import startsWith from 'lodash-es/startsWith';
import take from 'lodash-es/take';
import { Subscription } from 'rxjs';
import { Exponea } from './exponea.types';

@Injectable({
    providedIn: 'root'
})
export class ExponeaServiceDef {
    protected subscriptions: Subscription = new Subscription();

    /**
     * Initiation attempts counter, we have to wait for ga to be ready
     * @type {number}
     */
    protected initAttempts = 0;

    /**
     * Initiated flag
     * @type {number}
     */
    protected initiated = false;

    /**
     * Currently added coupon model
     * @type {string}
     */
    protected _promo: Exponea.ICurrentPromo = { code: null, value: 0 };

    @LocalStorage('checkoutBeforeSubmitData')
    protected checkoutBeforeSubmitData: Exponea.ICheckoutSubmitData | undefined;

    /**
     * events list waiting exponea to be initiated
     */
    protected waitingEvents: Exponea.IEvent[] = [];

    /**
     * Last path we tracked content view for
     */
    protected lastPath?: string;
    protected datePipe?: DatePipe;

    readonly app = inject(App);
    protected multisiteService = inject(MultisiteService);
    protected meta = inject(Meta);
    protected basketService = inject(BasketService);
    protected request = inject(RequestService);
    protected searchService = inject(SearchService);
    protected newsletterService = inject(NewsletterService);
    protected router = inject(Router);
    protected ngZone = inject(NgZone);
    protected location = inject(Location);
    protected customer = inject(CustomerModel);
    protected basket = inject(Basket);
    protected timeoutService = inject(TimeoutService);

    protected searchEventDebounced = this.timeoutService.debounce((search: SearchResult) => this.searchEvent(search), 1000);
    protected onMetaChangeDebounced = this.timeoutService.debounce(() => this.onMetaChange(), 500);

    constructor() {
        this.app.exponea = this;
        this.app.apiConfigService.loadApiConfig().then(() => {
            this.datePipe = new DatePipe(this.app.data.locale || 'en-GB');
        });

        this.init();
    }

    /////////////////////////////////////////////////
    ////////
    ////////    Init methods
    ////////
    ////////////////////////////////////////////////

    /**
     * Load script and initialize Exponea on success
     */
    protected init(): void {
        if (typeof window === 'undefined') {
            return;
        }
        if (!window.exponea) {
            window.exponea = { _: [] } as any as Exponea.IExponea;
            each(Exponea.KEYS, value => {
                window.exponea[value] = this.getPushFunction(value);
            });
        }
        this.setListeners();
        this.load(() => this.initialize());
    }

    /**
     * Loads exponea script
     * @param {ICallableFunction} onSuccess
     */
    protected load(onSuccess?: ICallableFunction): void {
        this.request.loadScript(this.getScriptName(), {
            onLoad: onSuccess,
            onScript: element => {
                element.setAttribute('class', 'optanon-category-C0004');
            }
        });
    }

    /**
     * Initialize exponea
     */
    protected initialize(): void {
        if (typeof window === 'undefined') {
            return;
        }
        if (!this.initiated) {
            const exponeaConfig = this.app.dfConfig.exponea;

            if (exponeaConfig?.token) {
                const onInit = () => {
                    if (window.exponea && exponeaConfig.token) {
                        window.exponea.initialize(exponeaConfig);
                        this.initiated = true;
                        this.waitingEvents.forEach(event => this.track(event));
                    }
                };

                if (exponeaConfig.track && exponeaConfig.track.google_analytics === false) {
                    onInit();
                } else {
                    if (window['ga'] && window['ga'].loaded) {
                        onInit();
                    } else if (this.initAttempts < 10) {
                        this.initAttempts++;
                        this.timeoutService.setTimeout(() => this.initialize(), 500);
                    } else {
                        this.initAttempts = 0;
                    }
                }
            }
        }
    }

    /**
     * Returns push event function
     * @param {string} eventName
     * @returns {() => void}
     */
    protected getPushFunction(eventName: string): () => void {
        if (typeof window === 'undefined') {
            return;
        }
        /* tslint:disable */
        return function () {
            const n = arguments;

            if ('initialize' == eventName && n && n[0].modify && n[0].modify.overlay && 'loading' == document.readyState) {
                const a = '__inf__overlay__';
                document.write(
                    '<div id="' +
                        a +
                        '" style="position:absolute;background:#fff;left:0;top:0;width:100%;height:100%;z-index:1000000"></div>'
                ),
                    this.timeoutService.setTimeout(function () {
                        const e = document.getElementById(a);
                        e && document.body.removeChild(e), (window.exponea.__ = !0);
                    }, n[0].modify.delay || 500);
            }

            this._.push([eventName, arguments]);
        };
        /* tslint:enable */
    }

    /**
     * Sets listeners for tracking
     */
    protected setListeners(): void {
        this.subscriptions.add(
            this.basketService.subscribeToAddToBasketSuccess((eventData: IAddToBasketSuccessEventData) => this.onBasketAdd(eventData))
        );
        this.subscriptions.add(
            this.basketService.subscribeToRemoveFromBasketSuccess((eventData: IRemoveFromBasketSuccessEventData) =>
                this.onBasketRemove(eventData)
            )
        );
        this.subscriptions.add(
            this.basketService.subscribeToChangeQtySuccess((eventData: IChangeQtyBasketItemSuccessEventData) =>
                this.onBasketItemQtyChange(eventData)
            )
        );
        this.subscriptions.add(
            this.searchService.subscribeToSearch((searchEvent: SearchResultEvent) => this.searchEventDebounced(searchEvent.result))
        );

        this.subscriptions.add(
            this.newsletterService.newsletterStatus.subscribe(event => {
                const subscribeRequest = event.data as Newsletter.ISubscribeRequest;
                const placement = subscribeRequest?.source;

                if (event instanceof NewsletterChangeEvent) {
                    this.onNewsletterConsentChange(event.subscribed, event.email || this.customer.data.email, placement);
                } else if (event instanceof NewsletterUpdateEvent) {
                    this.onNewsletterConsentChange(this.customer.data.newsletter, this.customer.data.email, placement);
                }
            })
        );

        this.subscriptions.add(
            this.app.events
                .on(WISHLIST_ADD_SUCCESS_EVENT)
                .subscribe((data: IAddToWishlistData) =>
                    this.onWishlistChange(Exponea.WishlistActions.Add, isArray(data.product) ? data.product : [data.product])
                )
        );
        this.subscriptions.add(
            this.app.events.on(WISHLIST_REMOVE_SUCCESS_EVENT).subscribe((productId: number | number[]) => {
                productId = Array.isArray(productId) ? productId : [productId];
                this.onWishlistChange(Exponea.WishlistActions.Remove, productId);
            })
        );
        this.subscriptions.add(
            this.app.events
                .on(Checkout.EVENTS.StepChange)
                .subscribe((data: Checkout.IStepChangeData) => this.onCheckoutStepChange(<any>data.checkout, data.stepBeforeChange))
        );

        this.subscriptions.add(
            this.app.event.success(event => {
                switch (event.id) {
                    case 'nexusCustomerRegister':
                        this.onRegister();
                        break;
                    case 'nexusCustomerLogin':
                    case 'nexusCheckoutLogin':
                        this.onLogin();
                        break;
                }
            })
        );
        this.subscriptions.add(
            this.app.events.on(Checkout.EVENTS.SuccessTracking).subscribe((data: Checkout.ISuccessData) => this.onCheckoutSuccess(data))
        );
        this.subscriptions.add(
            this.app.events.on(Account.CUSTOMER_DATA_UPDATE_EVENT).subscribe(() => {
                this.updateCustomerData();
                this.identifyCustomer();
            })
        );
        this.subscriptions.add(
            this.app.events
                .on(Checkout.EVENTS.SubmitBefore)
                .subscribe((checkoutSubmitBeforeData: Checkout.ISubmitBeforeEventData) =>
                    this.onCheckoutSubmitBefore(checkoutSubmitBeforeData)
                )
        );
        this.subscriptions.add(this.app.events.on(Checkout.EVENTS.SubmitError).subscribe(() => this.onCheckoutError()));

        this.subscriptions.add(this.basket.subscribe(() => this.onBasketUpdate()));
        this.customer.load().then(() => {
            this.onAuthentication();
            this.subscriptions.add(this.customer.obs.status.subscribe(() => this.onAuthentication()));
        });

        this.subscriptions.add(this.meta.subscribe(() => this.onMetaChangeDebounced()));
    }

    protected removeListeners() {
        this.subscriptions.unsubscribe();
    }

    /**
     * Returns exponea script name
     * @returns {string}
     */
    protected getScriptName(): string {
        return `${document.location.protocol}//api.exponea.com/js/exponea.min.js`;
    }

    protected updateCustomerData(customerData = this.getCustomerData()): void {
        if (typeof window === 'undefined') {
            return;
        }

        if (window.exponea) {
            this.app.apiConfigService
                .loadCountries()
                .then(() => {
                    window.exponea.update(customerData);
                })
                .catch(() => {});
        }
    }

    /**
     * Calls identify method
     * @param {string} identifier
     */
    protected identifyCustomer(identifier: string | number = this.customer.data.id) {
        if (typeof window === 'undefined') {
            return;
        }

        if (window.exponea && identifier) {
            window.exponea.identify(identifier.toString());
        }
    }

    protected identifyCustomerOnCheckoutSuccess(successData: Checkout.ISuccessData) {
        this.identifyCustomer(successData.customer.id || successData.customer.email);
    }

    /////////////////////////////////////////////////
    ////////
    ////////    Push exponea event method
    ////////
    ////////////////////////////////////////////////

    /**
     * Sends event to exponea
     * @param {Exponea.IEvent} event
     */
    track(event: Exponea.IEvent): void {
        if (typeof window === 'undefined') {
            return;
        }

        if (this.initiated) {
            window.exponea.track(
                event.name,
                pickBy(event.properties, value => value !== undefined && value !== '')
            );
        } else {
            this.waitingEvents.push(event);
        }
    }

    /////////////////////////////////////////////////
    ////////
    ////////    Exponea events
    ////////
    ////////////////////////////////////////////////

    /**
     * Sends category view event
     * @param {Category} category
     */
    protected viewCategoryEvent(category: Category<ICategoryDataV3>): void {
        if (category?.data) {
            const properties = this.getViewCategoryEventProperties(category);
            this.track({ name: Exponea.Events.ViewCategory, properties });
        }
    }

    /**
     * Sends product view event
     * @param {Product} product
     */
    protected viewProductEvent(product: Product<IProductDataV3>): void {
        if (product?.data) {
            const properties = this.getProductEventProperties(product);
            if (properties) {
                this.track({ name: Exponea.Events.ViewProduct, properties });
            }
        }
    }

    /**
     * Sends search event
     * @param {Search} search
     */
    protected searchEvent(search: SearchResult): void {
        if (search instanceof SearchResult) {
            const count = search.data?.count?.product;

            const properties: Exponea.ISearchEventProperties = {
                search_term: search.term || '',
                number_of_search_results: count || 0
            };
            this.track({ name: Exponea.Events.Search, properties });
        }
    }

    /**
     * Sends cart update event
     * @param {Exponea.IBasketItemCartUpdateEventProperties | Exponea.IPromoCartUpdateEventProperties} eventProperties
     */
    protected cartUpdateEvent(properties: Exponea.IBasketItemCartUpdateEventProperties | Exponea.IPromoCartUpdateEventProperties): void {
        this.track({ name: Exponea.Events.CartUpdate, properties });
    }

    /**
     * Sends checkout step event
     * @param {Exponea.IBasketItemCartUpdateEventProperties | Exponea.IPromoCartUpdateEventProperties} eventProperties
     */
    protected checkoutStepChangeEvent(properties: Exponea.ICheckoutStepChangeEventProperties): void {
        this.track({ name: Exponea.Events.Checkout, properties });
    }

    /**
     * Sends wishlist change event
     * @param {Exponea.IWishlistChangeEventProperties} eventProperties
     */
    protected wishlistChangeEvent(properties: Exponea.IWishlistChangeEventProperties): void {
        this.track({ name: Exponea.Events.Wishlist, properties });
    }

    /**
     * Sends newsletter consent event
     * @param {Exponea.INewsletterConsent} eventProperties
     */
    protected newsletterConsentEvent(properties: Exponea.INewsletterConsent): void {
        this.track({ name: Exponea.Events.NewsletterConsent, properties });
    }

    /**
     * Sends purchase item event
     * @param {Exponea.IPurchaseItemEventProperties} eventProperties
     */
    protected purchaseItemEvent(properties: Exponea.IPurchaseItemEventProperties): void {
        this.track({ name: Exponea.Events.PurchaseItem, properties });
    }

    /**
     * Sends authentication event
     * @param {Exponea.ICustomerAuthenticationEventProperties} eventProperties
     */
    protected authenticationEvent(properties: Exponea.ICustomerAuthenticationEventProperties): void {
        this.track({ name: Exponea.Events.Authentication, properties });
    }

    /**
     * Sends purchase event
     * @param {Exponea.IPurchaseEventProperties} eventProperties
     */
    protected purchaseEvent(properties: Exponea.IPurchaseEventProperties): void {
        this.track({ name: Exponea.Events.Purchase, properties });
    }

    /////////////////////////////////////////////////
    ////////
    ////////    Category events helpers
    ////////
    ////////////////////////////////////////////////

    /**
     * Returns view category event properties
     * @param {Category<ICategoryDataV3>} category
     * @returns {Exponea.IViewCategoryEventProperties}
     */
    protected getViewCategoryEventProperties(category: Category<ICategoryDataV3>): Exponea.IViewCategoryEventProperties {
        const breadcrumbs = category.data.breadcrumbs;

        return {
            category_id: category.data.id.toString(),
            category_name: category.data.name,
            category_listed_products: category.data.products ? category.data.products.map(id => id.toString()) : [],
            category_1: breadcrumbs && breadcrumbs.length ? breadcrumbs[0].label : '',
            categories_path: this.getCategoryCategoriesPath(category),
            categories_ids: this.getCategoryRootPath(category),
            local_currency: this.app.data.currency.base
        };
    }

    /////////////////////////////////////////////////
    ////////
    ////////    Cart update events helpers
    ////////
    ////////////////////////////////////////////////

    /**
     * On basket data update callback
     */
    protected onBasketUpdate(): void {
        const coupons = this.basket.data.coupon;
        const promo: Exponea.ICurrentPromo = { code: isArray(coupons) ? coupons[0] : coupons, value: this.basket.data.coupon_value || 0 };
        if (this.promo.code === null) {
            this._promo = promo;
        } else {
            this.promo = promo;
        }
    }

    /**
     * On add to basket callback
     * @param {IAddToBasketSuccessEventData} requestData
     */
    protected onBasketAdd(requestData: IAddToBasketSuccessEventData): void {
        const basketSubscription = this.basket.subscribe(() => {
            basketSubscription.unsubscribe();
            const addRequests = requestData.request.products;
            addRequests.forEach(request => {
                const basketItem = this.basket.byAddToBasketRequest(request);
                if (basketItem) {
                    this.onBasketItemsChange(Exponea.CartUpdateActions.Add, basketItem);
                }
            });
        });
    }

    /**
     * On remove from basket callback
     * @param {IRemoveFromBasketSuccessEventData} requestData
     */
    protected onBasketRemove(requestData: IRemoveFromBasketSuccessEventData): void {
        const basketItems = requestData.items;
        if (basketItems?.length) {
            basketItems.forEach(basketItem => this.onBasketItemsChange(Exponea.CartUpdateActions.Remove, basketItem));
        }
    }

    /**
     * On basket item qty change callback
     * @param {ISetQtyBasketItemSuccessEventData} requestData
     */
    protected onBasketItemQtyChange(requestData: IChangeQtyBasketItemSuccessEventData): void {
        const id = requestData.request.id;
        const basketSubscription = this.basket.subscribe(() => {
            basketSubscription.unsubscribe();
            const basketItem = this.basket.byId(id);
            if (basketItem) {
                this.onBasketItemsChange(Exponea.CartUpdateActions.Quantity, basketItem);
            }
        });
    }

    /**
     * Current promo setter
     * @param {Exponea.ICurrentPromo} promo
     */
    protected set promo(promo: Exponea.ICurrentPromo) {
        const previousPromo = this.promo;
        this._promo = promo;
        if (previousPromo.code !== this.promo.code) {
            const action = this.promo.code ? Exponea.CartUpdateActions.PromoAdd : Exponea.CartUpdateActions.PromoRemove;
            const changedPromo = (promo.code || previousPromo.code) as string;
            const changedValue = promo.value || previousPromo.value;
            this.onPromoChange(action, changedPromo, changedValue);
        }
    }

    /**
     * Current promo getter
     * @returns {Exponea.ICurrentPromo}
     */
    protected get promo(): Exponea.ICurrentPromo {
        return this._promo;
    }

    /**
     * Returns common properties for cart update events
     * @param {Exponea.CART_UPDATE_ACTIONS} action
     * @returns {Exponea.ICartUpdateEventProperties}
     */
    protected getCartUpdateEventProperties(action: Exponea.CartUpdateActions): Exponea.ICartUpdateEventProperties {
        const productList = this.getProductList();
        return {
            action,
            page_type: this.getPageType(),
            product_list: productList,
            product_ids: productList.map(item => item.product_id),
            total_quantity: this.basket.data.qty,
            total_price: this.basket.data.totals?.grand_total || 0,
            total_price_local_currency: this.basket.data.totals?.grand_total
        };
    }

    /**
     * On basket items change(add, remove, qty change) callback
     * @param {Exponea.ItemsCartUpdateAction} action
     * @param basketItem
     */
    protected onBasketItemsChange(action: Exponea.ItemsCartUpdateAction, basketItem: BasketItem): void {
        const eventProperties = this.getBasketItemEventProperties(action, basketItem);
        if (eventProperties) {
            this.cartUpdateEvent(eventProperties);
        }
    }

    /**
     * Returns event properties for basket items cart update event
     * @param {Exponea.ItemsCartUpdateAction} action
     * @param {BasketItem} basketItem
     * @returns {Exponea.IProductCartUpdateEventProperties | undefined}
     */
    protected getBasketItemEventProperties(
        action: Exponea.ItemsCartUpdateAction,
        basketItem: BasketItem
    ): Exponea.IBasketItemCartUpdateEventProperties | undefined {
        const product = basketItem.product;
        if (product?.data) {
            const price = basketItem.price;
            const orgPrice = basketItem.orgPrice;
            const discount = orgPrice && price && orgPrice > price ? orgPrice - price : 0;

            const { coupon, coupon_value } = this.basket.data;

            const eventProperties: Exponea.IBasketItemCartUpdateEventProperties = {
                ...this.getCartUpdateEventProperties(action),
                ...this.getProductExponeaEventProperties(product),
                price,
                price_local_currency: price,
                discount_value: discount,
                original_price: orgPrice || price,
                original_price_local_currency: orgPrice || price,
                local_currency: this.app.data.currency.base,
                promo_code: isArray(coupon) ? coupon[0] : coupon,
                promo_value: coupon_value
            };
            return eventProperties;
        }
        return undefined;
    }

    protected getPurchaseItemEventProperties(
        basketItem: BasketItem,
        successData: Checkout.ISuccessData,
        basketData: IBasketData
    ): Exponea.IPurchaseItemEventProperties | undefined {
        const product = basketItem.product;
        if (product?.data) {
            const productProperties = this.getProductExponeaEventProperties(product);
            delete productProperties.category_id;
            delete productProperties.category_2;
            delete productProperties.category_3;

            const price = basketItem.price;
            const orgPrice = basketItem.orgPrice;
            const discount = orgPrice && price && orgPrice > price ? orgPrice - price : 0;

            const purchaseId = successData.number || successData.numbers?.[0];

            const eventProperties: Exponea.IPurchaseItemEventProperties = {
                ...productProperties,
                purchase_id: purchaseId,
                purchase_status: successData.status,
                price,
                price_local_currency: price,
                discount_value: discount,
                discount_percentage: discount ? (discount / price) * 100 : 0,
                original_price: orgPrice || price,
                original_price_local_currency: orgPrice || price,
                local_currency: this.app.data.currency.base,
                quantity: basketItem.qty,
                total_price: basketData.totals.grand_total_global || basketData.totals.grand_total,
                total_price_without_tax: this.getTotalWithoutTax(basketData),
                total_price_local_currency: basketData.totals.grand_total
            };
            return eventProperties;
        }
    }

    protected getTotalWithoutTax(basketData: IBasketData = this.basket.data): number {
        if (basketData.totals) {
            const { grand_total, tax } = basketData.totals;
            if (tax) {
                return Math.round((grand_total - tax) * 100) / 100;
            }
            return grand_total;
        }
        return 0;
    }

    protected getPurchaseEventProperties(
        checkoutSubmitData: Exponea.ICheckoutSubmitData,
        successData: Checkout.ISuccessData
    ): Exponea.IPurchaseEventProperties {
        const { basketData, submissionData, checkoutData } = checkoutSubmitData;
        const promoCodes = basketData.coupon;

        const productList = this.getProductList(basketData);
        const eventProperties: Exponea.IPurchaseEventProperties = {
            purchase_id: successData.number || successData.numbers?.[0],
            purchase_status: successData.status,
            promo_code: Array.isArray(promoCodes) ? promoCodes[0] : promoCodes,
            promo_value: basketData.coupon_value,
            payment_type: this.parsePaymentMethod(submissionData.paymentMethod.method?.toString()),
            delivery_type: checkoutData.shipping.method,
            delivery_cost: basketData.totals.shipping,
            delivery_country: this.getCountryByCode(checkoutData.address.shipping?.country_id),
            delivery_city: checkoutData.address.shipping && checkoutData.address.shipping.city,
            tax_value: basketData.totals.tax,
            local_currency: this.app.data.currency.base,
            product_list: productList,
            product_ids: productList.map(product => product.product_id),
            total_quantity: basketData.qty,
            total_price: basketData.totals.grand_total_global || basketData.totals.grand_total,
            total_price_without_tax: this.getTotalWithoutTax(basketData),
            total_price_local_currency: basketData.totals.grand_total
        };
        return eventProperties;
    }

    protected parsePaymentMethod(code: string): string {
        return code;
    }

    /**
     * On promo coupon change callback
     * @param {Exponea.PromoCartUpdateAction} action
     * @param {string} promoCode
     */
    protected onPromoChange(action: Exponea.PromoCartUpdateAction, promoCode: string, promoValue: number): void {
        const eventProperties: Exponea.IPromoCartUpdateEventProperties = {
            ...this.getCartUpdateEventProperties(action),
            promo_code: promoCode,
            promo_value: promoValue
        };
        this.cartUpdateEvent(eventProperties);
    }

    /////////////////////////////////////////////////
    ////////
    ////////    Checkout methods
    ////////
    ////////////////////////////////////////////////

    /**
     * On checkout step change callback
     * @param checkout
     * @param stepBeforeName
     */
    protected onCheckoutStepChange(checkout: CheckoutFormComponentDef, stepBeforeName: string): void {
        const eventProperties = this.getCheckoutStepEventProperties(checkout, stepBeforeName);
        if (eventProperties) {
            this.checkoutStepChangeEvent(eventProperties);
        }
    }

    protected getCheckoutStepEventProperties(
        checkout: CheckoutFormComponentDef,
        stepBeforeName: string
    ): Exponea.ICheckoutStepChangeEventProperties | undefined {
        const stepBefore = checkout.getStepByName(stepBeforeName);

        if (stepBefore && checkout.currentStep) {
            const stepBeforeOrder = stepBefore.order;
            const currentStepOrder = checkout.currentStep.order;

            // we want to track completeness of steps
            if (stepBeforeOrder && currentStepOrder && currentStepOrder >= stepBeforeOrder) {
                const productList = this.getProductList();
                const paymentMethod = checkout.data.payment.method ? checkout.paymentMethodByCode(checkout.data.payment.method) : undefined;
                const promoCodes = this.basket.data.coupon;
                const eventProperties: Exponea.ICheckoutStepChangeEventProperties = {
                    step_title: stepBefore.stepTitle,
                    promo_code: isArray(promoCodes) ? promoCodes[0] : promoCodes,
                    promo_total_value: this.basket.data.coupon_value,
                    payment_type: this.parsePaymentMethodTitle(paymentMethod?.['title'], paymentMethod?.['code']),
                    delivery_type: checkout.data.shipping.method,
                    delivery_cost: this.basket.data.totals?.shipping,
                    delivery_country:
                        checkout.data.address.shipping.country || this.getCountryByCode(checkout.data.address.shipping.country_id),
                    delivery_city: checkout.data.address.shipping?.city,
                    tax_value: this.basket.data.totals && this.basket.data.totals.tax,
                    local_currency: this.app.data.currency.base,
                    product_list: productList,
                    product_ids: productList.map(item => item.product_id),
                    total_quantity: this.basket.data.qty,
                    total_price: this.basket.data.totals?.grand_total_global || this.basket.data.totals?.grand_total,
                    total_price_without_tax: this.getTotalWithoutTax(),
                    total_price_local_currency: this.basket.data.totals?.grand_total
                };

                return eventProperties;
            }
        }

        return undefined;
    }

    protected onCheckoutCompletedStep(data: Exponea.ICheckoutSubmitData): void {
        const { basketData, submissionData, checkoutData, checkoutBackendData } = data;
        const promoCodes = basketData.coupon;
        const paymentMethodCode = submissionData.paymentMethod.method;
        const paymentMethod = paymentMethodCode
            ? find(checkoutBackendData.payment.methods, method => method.code === paymentMethodCode)
            : undefined;
        const productList = this.getProductList(basketData);
        const eventProperties: Exponea.ICheckoutStepChangeEventProperties = {
            step_title: 'Completed',
            promo_code: isArray(promoCodes) ? promoCodes[0] : promoCodes,
            promo_total_value: basketData.coupon_value,
            payment_type: this.parsePaymentMethodTitle(paymentMethod?.['title'], paymentMethod?.['code']),
            delivery_type: checkoutData.shipping.method,
            delivery_cost: basketData.totals?.shipping,
            delivery_country: checkoutData.address.shipping.country || this.getCountryByCode(checkoutData.address.shipping.country_id),
            delivery_city: checkoutData.address.shipping.city,
            tax_value: basketData.totals && basketData.totals.tax,
            local_currency: this.app.data.currency.base,
            product_list: productList,
            product_ids: productList.map(item => item.product_id),
            total_quantity: basketData.qty,
            total_price: basketData.totals?.grand_total_global || basketData.totals?.grand_total,
            total_price_without_tax: this.getTotalWithoutTax(basketData),
            total_price_local_currency: basketData.totals && basketData.totals.grand_total
        };
        this.checkoutStepChangeEvent(eventProperties);
    }

    protected parsePaymentMethodTitle(title?: string, _code?: string | number): string {
        return title || '';
    }

    protected onPurchase(checkoutBeforeSubmitData: Exponea.ICheckoutSubmitData, successData: Checkout.ISuccessData) {
        const items = checkoutBeforeSubmitData.basketData.items;
        const ids = items.map(item => item.product);

        Promise.all([this.app.resources.product.loadAllIds(ids, 3, { safe: true }), this.app.apiConfigService.loadCountries()]).finally(
            () => {
                items.forEach(item => {
                    const basketItem = new BasketItem(item, this.basket);
                    const eventProperties = this.getPurchaseItemEventProperties(
                        basketItem,
                        successData,
                        checkoutBeforeSubmitData.basketData
                    );
                    if (eventProperties) {
                        this.purchaseItemEvent(eventProperties);
                    }
                });
                if (this.app.apiConfigService.countries) {
                    this.purchaseEvent(this.getPurchaseEventProperties(checkoutBeforeSubmitData, successData));
                }
            }
        );
    }

    protected onCheckoutSubmitBefore(checkoutSubmitBeforeData: Checkout.ISubmitBeforeEventData): void {
        this.checkoutBeforeSubmitData = {
            submissionData: cloneDeep(checkoutSubmitBeforeData.data),
            customerData: cloneDeep(this.customer.data),
            basketData: cloneDeep(this.basket.data),
            checkoutBackendData: cloneDeep((<any>checkoutSubmitBeforeData.checkout).backendData),
            checkoutData: cloneDeep((<any>checkoutSubmitBeforeData.checkout).data)
        };
    }

    /**
     * On checkout success callback
     * @param {ISuccessData} successData
     */
    protected onCheckoutSuccess(successData: Checkout.ISuccessData): void {
        if (this.checkoutBeforeSubmitData) {
            const checkoutBeforeSubmitData = cloneDeep(this.checkoutBeforeSubmitData);
            this.checkoutBeforeSubmitData = undefined;

            this.app.apiConfigService
                .loadCountries()
                .then(() => {
                    this.onCheckoutCompletedStep(checkoutBeforeSubmitData);
                    this.onPurchase(checkoutBeforeSubmitData, successData);
                })
                .catch(() => {});

            this.onCheckoutSuccessCallback(successData, checkoutBeforeSubmitData);
        }
    }

    protected onCheckoutSuccessCallback(
        successData: Checkout.ISuccessData,
        checkoutBeforeSubmitData: Exponea.ICheckoutSubmitData,
        placement = 'checkout_success'
    ): void {
        if (!this.customer.isLoggedIn) {
            const { checkoutData } = checkoutBeforeSubmitData;

            this.onNewsletterConsentChange(checkoutData.newsletter, successData.customer.email, placement);
            this.identifyCustomerOnCheckoutSuccess(successData);
            this.updateCustomerData(this.getCustomerDataFromCheckoutData(checkoutBeforeSubmitData, successData));
        }
    }

    protected onCheckoutError(): void {
        this.checkoutBeforeSubmitData = undefined;
    }

    /////////////////////////////////////////////////
    ////////
    ////////    Wishlist methods
    ////////
    ////////////////////////////////////////////////

    /**
     * On wishlist add/remove callback
     * @param {Exponea.WISHLIST_ACTIONS} action
     * @param {number[]} ids
     */
    protected onWishlistChange(action: Exponea.WishlistActions, ids: number[]): void {
        ids.forEach(id => {
            const product = this.app.resources.product.getExisting(id) as Product<IProductDataV2>;
            if (product) {
                const eventProperties = this.getProductEventProperties(product);
                if (eventProperties) {
                    delete eventProperties.stock_level;
                    this.wishlistChangeEvent({ ...eventProperties, action } as Exponea.IWishlistChangeEventProperties);
                }
            }
        });
    }

    /////////////////////////////////////////////////
    ////////
    ////////    Newsletter consent methods
    ////////
    ////////////////////////////////////////////////

    protected onNewsletterConsentChange(value: any, email: string, placement?: string, message?: string) {
        const action = this.getNewsletterAction(value);
        const eventProperties: Exponea.INewsletterConsent = {
            action,
            category: 'weekly_newsletters_from_web',
            timestamp: Date.now(),
            identification_type: 'email',
            identification: email,
            source: 'public_api',
            valid_until: 'unlimited',
            message: message || '',
            placement: placement || 'public_api',
            optin_date: this.datePipe.transform(new Date(), 'dd/MM/yy'),
            current_website: this.multisiteService?.current?.name || ''
        };
        this.newsletterConsentEvent(eventProperties);
    }

    /////////////////////////////////////////////////
    ////////
    ////////    Customer data methods
    ////////
    ////////////////////////////////////////////////

    /**
     * On register callback
     */
    protected onRegister(): void {
        this.onNewsletterConsentChange(this.customer.data.newsletter, this.customer.data.email as string, 'register');
        this.updateCustomerData();
        this.identifyCustomer();
    }

    protected onLogin(): void {
        this.identifyCustomer();
    }

    protected onAuthentication(): void {
        const loggedIn = this.customer.isLoggedIn;
        const { id, email } = this.customer.data;
        const eventProperties: Exponea.ICustomerAuthenticationEventProperties = {
            logged_in: loggedIn,
            user_id: loggedIn ? id && id.toString() : undefined,
            email: loggedIn ? email : undefined
        };
        this.authenticationEvent(eventProperties);
    }

    protected getNewsletterAction(value: any): Exponea.NewsletterConsentActions {
        const { Accept, Reject } = Exponea.NewsletterConsentActions;
        return value ? Accept : Reject;
    }

    /////////////////////////////////////////////////
    ////////
    ////////    Recommendations methods
    ////////
    ////////////////////////////////////////////////

    loadRecommendationsProducts(options: string | Exponea.IRecommendationsOptions, productSku?: string[]): Promise<Product[]> {
        return new Promise((resolve, reject) => {
            this.loadRecommendationsProductIds(options, productSku)
                .then(ids => {
                    if (ids?.length) {
                        this.app.resources.product
                            .loadAllIds(ids, 2, { safe: true })
                            .then(() => {
                                resolve(this.app.resources.product.getAllExisting(ids));
                            })
                            .catch(reject);
                    } else {
                        resolve([]);
                    }
                })
                .catch(reject);
        });
    }

    loadRecommendationsProductIds(options: string | Exponea.IRecommendationsOptions, productSku?: string[]): Promise<number[]> {
        return new Promise((resolve, reject) => {
            this.loadRecommendations(options, productSku)
                .then(items => {
                    const productIds = items?.length ? compact(items.map(item => item.item_id && parseInt(item.item_id, 10))) : [];
                    resolve(productIds);
                })
                .catch(reject);
        });
    }

    protected loadRecommendations(
        options: string | Exponea.IRecommendationsOptions,
        productSku?: string[]
    ): Promise<Exponea.IRecommendationsItem[]> {
        return new Promise((resolve, reject) => {
            const recommendationsOptions = this.getRecommendationsOptions(options, productSku);

            if (recommendationsOptions) {
                const promise = this.getRecommendations(recommendationsOptions);
                if (promise) {
                    promise.then(resolve).catch(reject);
                } else {
                    reject();
                }
            } else {
                reject();
            }
        });
    }

    /**
     *  Returns recommendations options
     * @param {string}  snippet
     * @param {string}  productSku
     * @returns {Exponea.IRecommendationsOptions | undefined}
     */
    private getRecommendationsOptions(
        options: string | Exponea.IRecommendationsOptions,
        productSku?: string[]
    ): Exponea.IRecommendationsOptions | undefined {
        if (options) {
            const recommendationsOptions = typeof options === 'string' ? this.parseRecommendationsSnippet(options) : options;
            if (recommendationsOptions) {
                if (productSku?.length) {
                    recommendationsOptions.items = {};
                    productSku.forEach(sku => {
                        recommendationsOptions.items[sku] = 1;
                    });
                }
                return recommendationsOptions;
            }
        }
        return undefined;
    }

    /**
     * Loads recommendations
     * @param {Exponea.IRecommendationsOptions} options
     * @returns {Promise<Exponea.IRecommendationsItem[]>}
     */
    private getRecommendations(options: Exponea.IRecommendationsOptions): Promise<Exponea.IRecommendationsItem[] | null> | undefined {
        if (typeof window === 'undefined') {
            return undefined;
        }
        return new Promise((resolve, reject) => {
            if (window.exponea) {
                options = { ...options };
                options.callback = resolve;

                try {
                    window.exponea.getRecommendation(options);
                } catch (e) {
                    this.app.logger.error(e, options);
                    reject();
                }
            } else {
                reject();
            }
        });
    }

    /**
     * Parses snippet from exponea admin panel to get recommendations options
     * @param {string} snippetEntered
     * @returns {Exponea.IRecommendationsOptions}
     */
    parseRecommendationsSnippet(snippetEntered: string): Exponea.IRecommendationsOptions | undefined {
        // we don't know what exactly comes in
        try {
            let snippet: string | string[] = snippetEntered;
            const optionsIndex = snippet.indexOf('var options');
            // we remove part of snippet before var options
            snippet = optionsIndex !== -1 ? snippet.substr(optionsIndex) : snippet;
            const exponeaRecommendationIndex = snippet.indexOf('exponea.getRecommendation');
            // we remove part of snippet after options
            snippet = exponeaRecommendationIndex !== -1 ? snippet.substr(0, exponeaRecommendationIndex) : snippet;
            snippet = snippet.trim();
            const bracketIndex = snippet.indexOf('{');
            // if there is no open bracket string is invalid
            if (bracketIndex === -1) {
                return undefined;
            }
            // snippet = snippet.substr(bracketIndex);
            // we check close brakcet
            snippet = snippet.split('').reverse();
            const endBracketIndex = snippet.indexOf('}');
            // if no close bracket string is invalid
            if (endBracketIndex === -1) {
                return undefined;
            }
            // we remove what is after close bracket
            snippet = snippet.slice(endBracketIndex);
            snippet = snippet.reverse().join('');

            // tslint:disable-next-line: prefer-const
            let options;
            snippet = snippet.replace('onRecommendationsLoaded', '""').replace('var', '');
            // tslint:disable-next-line: no-eval
            eval(snippet);
            if (options) {
                return options as Exponea.IRecommendationsOptions;
            } else {
                return undefined;
            }
        } catch (e) {
            this.app.logger.error(e);
            return undefined;
        }
    }

    /////////////////////////////////////////////////
    ////////
    ////////    Helper methods
    ////////
    ////////////////////////////////////////////////

    /**
     * Returns page type
     * @returns {string}
     */
    protected getPageType(): string {
        const model = this.meta.model;

        if (model && model.data) {
            switch (model.data.type) {
                case 'product':
                case 'category':
                    return model.data.type;
                case 'page':
                    return (model as IPage<IPageDataV3>).data.name;
            }
        }
        const url = this.router.url.toLowerCase();
        switch (true) {
            case startsWith(url, routeOverride('/account')):
                return 'account';
            case startsWith(url, routeOverride('/checkout')):
                return 'checkout';
            case startsWith(url, routeOverride('/basket')):
                return 'basket';
        }
    }

    /**
     * Returns category root path
     * @param {Category | Product} model
     * @returns {string[]}
     */
    protected getCategoryRootPath(model: Category<ICategoryDataV2> | Product<IProductDataV2>): string[] {
        const categoryRootPath = model.data.category_root_path;
        if (categoryRootPath) {
            return categoryRootPath.map(id => id.toString());
        }
        return [];
    }

    /**
     * Returns categories path for category
     * @param {Category} category
     * @returns {string}
     */
    protected getCategoryCategoriesPath(category: Category<ICategoryDataV2>): string {
        const breadcrumbs = category.data.breadcrumbs;
        if (breadcrumbs) {
            return this.getCategoriesPath(breadcrumbs);
        }
        return '';
    }

    /**
     * Returns categories path for product
     * @param {Product} product
     * @returns {string}
     */
    protected getProductCategoriesPath(product: Product<IProductDataV2>): string {
        const breadcrumbs = product.data.breadcrumbs;
        if (breadcrumbs) {
            return this.getCategoriesPath(take(breadcrumbs, breadcrumbs.length - 1));
        }
        return '';
    }

    /**
     * Returns categories path
     * @param {ICrumb[]} breadcrumbs
     * @returns {string}
     */
    protected getCategoriesPath(breadcrumbs: ICrumb[]): string {
        return breadcrumbs.map(breadcrumb => breadcrumb.label).join('>');
    }

    /**
     * Returns product exponea event properties
     * @param {Product} product
     * @returns {Exponea.IProductEventProperties}
     */
    protected getProductExponeaEventProperties(product: Product<IProductDataV2>): Exponea.IProductEventProperties {
        function getCategoryBreadcrumbs(breadcrumbs?: ICrumb[]): { category_1: string; category_2: string; category_3: string } {
            const categoryBreadcrumbs = { category_1: '', category_2: '', category_3: '' };
            if (breadcrumbs) {
                take(take(breadcrumbs, breadcrumbs.length - 1), 3).forEach((breadcrumb, index) => {
                    categoryBreadcrumbs[`category_${index + 1}`] = breadcrumb.label;
                });
            }
            return categoryBreadcrumbs;
        }

        const categoryId = product.data.categories?.last;
        return {
            product_id: product.data.id.toString(),
            title: (product.data.name || '').trim(),
            ...getCategoryBreadcrumbs(product.data.breadcrumbs),
            categories_path: this.getProductCategoriesPath(product),
            category_id: categoryId ? categoryId.toString() : '',
            categories_ids: this.getCategoryRootPath(product)
        } as Exponea.IProductEventProperties;
    }

    /**
     * Returns basket product list
     * @returns {Exponea.ICartProductListItem[]}
     */
    protected getProductList(basketData = this.basket.data): Exponea.ICartProductListItem[] {
        return basketData.items.map(item => {
            return {
                product_id: item.product.toString(),
                quantity: item.request.qty
            };
        });
    }

    /**
     * Returns product event properties
     * @param {Product} product
     * @returns {Exponea.IViewProductEventProperties | undefined}
     */
    protected getProductEventProperties(product: Product<IProductDataV2>): Exponea.IViewProductEventProperties | undefined {
        if (product?.data) {
            const orgPrice = product.orgPrice;
            const price = product.price;
            const discount = orgPrice && price && orgPrice > price ? orgPrice - price : 0;
            const stock = product.stock as Stock;

            return {
                ...this.getProductExponeaEventProperties(product),
                price,
                price_local_currency: price,
                discount_value: discount,
                original_price: orgPrice || price,
                original_price_local_currency: orgPrice || price,
                local_currency: this.app.data.currency.base,
                stock_level: stock?.qty
            };
        }

        return undefined;
    }

    /**
     * Returns exponea customer update data from customer data
     * @returns {Exponea.ICustomerUpdate}
     */
    protected getCustomerData(): Exponea.ICustomerUpdate {
        const customerData: Exponea.ICustomerUpdate = {
            first_name: this.customer.data.firstname,
            last_name: this.customer.data.lastname,
            email: this.customer.data.hash,
            title: this.customer.data.prefix,
            gender: this.customer.data.gender
        };

        const dob = toDate(this.customer.data.dob);
        if (dob) {
            // We take seconds and add 6 hours to avoid summer time shifting the date to previous day.
            customerData.birth_date = dob.getTime() / 1000 + 3600;
        }

        const defaultAddress = this.app.customer.addressBook?.shipping;
        if (defaultAddress) {
            customerData.city = defaultAddress.data.city;
            customerData.country = this.getCountryByCode(defaultAddress.data.country_id);
            customerData.phone = defaultAddress.data.telephone;
        }
        return customerData;
    }

    /**
     * Returns exponea customer update data from checkout success data
     * @param {Exponea.ICheckoutSubmitData} checkoutData
     * @param {Checkout.ISuccessData} successData
     * @returns {Exponea.ICustomerUpdate}
     */
    protected getCustomerDataFromCheckoutData(
        checkoutData: Exponea.ICheckoutSubmitData,
        successData: Checkout.ISuccessData
    ): Exponea.ICustomerUpdate {
        const { submissionData } = checkoutData;
        const address = submissionData.shipping_address || submissionData.billing_address;
        const customerData: Exponea.ICustomerUpdate = {
            email: successData.hash
        };

        if (address) {
            customerData.first_name = address.firstname;
            customerData.last_name = address.lastname;
            customerData.title = address.title;
            customerData.city = address.city;
            customerData.country = this.getCountryByCode(address.country_id);
            customerData.phone = address.telephone;
        }
        return customerData;
    }

    protected onMetaChange() {
        const path = this.meta.getMetaProperty('canonical') || this.meta.getMetaProperty('url') || this.location.path();

        if (this.lastPath !== path) {
            this.lastPath = path;
            const model = this.meta.model;
            if (model) {
                switch (model.type) {
                    case 'category':
                        this.viewCategoryEvent(model as Category<ICategoryDataV3>);
                        break;
                    case 'product':
                        this.viewProductEvent(model as Product<IProductDataV3>);
                        break;
                }
            }
        }
    }

    protected getCountryByCode(code: string): string | undefined {
        const country =
            this.app.apiConfigService.countries &&
            code &&
            find(this.app.apiConfigService.countries, iCountry => iCountry.code.toLowerCase() === code.toLowerCase());
        return country && country.name;
    }

    sendBackInStockEvent(variant_id: string): void {
        const properties: Exponea.IStockNotificationEventProperties = {
            variant_id,
            store_id: this.app.multisite.current?.country
        };

        this.track({ name: Exponea.Events.StockNotification, properties });
    }
}
